import { useIsFocused } from '@react-navigation/native';
import { Button, Icon, Spinner, Text, useTheme } from '@ui-kitten/components';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, View } from 'react-native';

import ActionItem from './ActionItem';
import ActionItemLabels from './ActionItemLabels';
import useResponsiveStyleSheet, {
  createResponsiveStyle,
} from '../../hooks/useResponsiveStyleSheet';
import { useStore } from '../../stores';
import { ActionItemEntryData, FilterData } from '../../types';
import ShowFilters from '../Common/ShowFilters';

type Props = {
  index: number;
};

const ActionItems: React.FC<Props> = ({ index }) => {
  const styles = useResponsiveStyleSheet(themedStyles);
  const store = useStore();
  const { assessmentStore, isInternetReachable } = store;
  const isFocused = useIsFocused();
  const theme = useTheme();

  const [actions, setActions] = useState<ActionItemEntryData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFooter, setLoadingFooter] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const page = useRef(-1);
  const loadingRef = useRef(false);
  const filters = store.filters || {};

  useEffect(() => {
    if (isFocused && index === 0) {
      onFilterSubmit(filters);
    }
  }, [isFocused, index]);

  const onFilterSubmit = async (data: FilterData) => {
    if (
      loading ||
      loadingRef.current ||
      loadingFooter ||
      !isInternetReachable
    ) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    page.current = 1;
    const result = await assessmentStore.fetchActionItems(data, page.current);
    if (result.ok) {
      setActions(result.extra?.answers as ActionItemEntryData[]);

      if (result.extra && result.extra.next) {
        page.current += 1;
      } else {
        page.current = -1;
      }

      if (result.extra?.count) {
        setTotalCount(result.extra.count);
      }
    } else {
      page.current = -1;
      setLoadingFooter(false);
    }

    loadingRef.current = false;
    setLoading(false);
  };

  const fetchNextActionItems = async () => {
    if (
      loading ||
      loadingFooter ||
      loadingRef.current ||
      page.current === -1 ||
      !isInternetReachable
    ) {
      return;
    }

    setLoadingFooter(true);

    const result = await assessmentStore.fetchActionItems(
      filters,
      page.current,
    );

    if (result.ok) {
      setActions([
        ...actions,
        ...(result.extra?.answers as ActionItemEntryData[]),
      ]);

      if (result.extra?.next === null) {
        page.current = -1;
      } else {
        page.current += 1;
      }
    } else {
      page.current = -1;
    }

    setLoadingFooter(false);
  };

  const renderActions = useCallback(
    ({ item }: { item: ActionItemEntryData }) => (
      <ActionItem actionItem={item} />
    ),
    [isInternetReachable],
  );

  const renderEmptyAssessments = () =>
    loading ? (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={theme['bp-green']} />
      </View>
    ) : (
      <Text style={styles.emptyAssessments}>No action items found</Text>
    );

  const renderFooter = () => {
    if (loading || actions.length === 0) {
      return;
    }

    return (
      <View style={styles.footerContainer}>
        {totalCount ? (
          <Text style={styles.footerSummary}>
            Showing {actions.length} of {totalCount} action items
          </Text>
        ) : (
          <></>
        )}
        {page.current !== -1 ? (
          <Button
            status="primary"
            appearance="filled"
            accessoryLeft={(props) =>
              loadingFooter ? (
                <Spinner status="info" {...props} />
              ) : (
                <Icon
                  name="plus-outline"
                  fill={theme['text-white']}
                  {...props}
                />
              )
            }
            onPress={() => fetchNextActionItems()}
          >
            Load more action items
          </Button>
        ) : (
          <></>
        )}
      </View>
    );
  };

  const renderHeader = () => (
    <>
      {isInternetReachable && (
        <ShowFilters
          onFilterSubmit={onFilterSubmit}
          actualIndex={index}
          filterIndex={0}
        />
      )}
      <ActionItemLabels />
    </>
  );

  const renderSeparator = useCallback(
    () => <View style={styles.separator} />,
    [],
  );

  return (
    <View style={styles.container}>
      <FlatList
        data={loading ? [] : actions}
        renderItem={renderActions}
        contentContainerStyle={[{ paddingBottom: 80, paddingRight: 26 }]}
        keyExtractor={(_item, index) => `${index}`}
        ItemSeparatorComponent={renderSeparator}
        ListHeaderComponent={renderHeader()}
        ListEmptyComponent={renderEmptyAssessments()}
        ListFooterComponent={renderFooter()}
        extraData={isInternetReachable}
      />
    </View>
  );
};

const themedStyles = createResponsiveStyle({
  baseStyle: {
    container: {
      flex: 1,
      paddingLeft: 26,
    },
    emptyAssessments: {
      textAlign: 'center',
      marginTop: 20,
      fontFamily: 'UniversLTPro_Regular',
    },
    separator: {
      backgroundColor: 'gray-03',
      height: 1,
    },
    loading: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    footerContainer: {
      marginTop: 20,
      alignItems: 'center',
    },
    footerSummary: {
      fontSize: 14,
      fontFamily: 'UniversBP_Light',
      fontWeight: 'bold',
      color: '#232323',
      marginBottom: 10,
    },
  },
});

export default observer(ActionItems);
